import React, { useState, useEffect, useCallback } from 'react';
import { api, formatApiError } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Upload, MagnifyingGlass, Trash, Scan, Eye, Files } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [invoiceType, setInvoiceType] = useState('purchase');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [scanning, setScanning] = useState(null);
  const [multiMode, setMultiMode] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [invoicesRes, clientsRes] = await Promise.all([
        api.get('/invoices'),
        api.get('/clients'),
      ]);
      setInvoices(invoicesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !selectedClient) return;

    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });
    
    const uploadedInvoices = [];
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress({ current: i + 1, total: files.length });
      
      const formData = new FormData();
      formData.append('file', file);

      try {
        if (multiMode) {
          // Multi-invoice scan: extract multiple invoices from single document
          const { data } = await api.post(
            `/invoices/upload-multi?client_id=${selectedClient}&invoice_type=${invoiceType}`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          if (data.invoices) {
            uploadedInvoices.push(...data.invoices);
          }
          successCount += data.count || 0;
          toast.success(`${data.count} invoice(s) extracted from ${file.name}`);
        } else {
          const { data } = await api.post(
            `/invoices/upload?client_id=${selectedClient}&invoice_type=${invoiceType}`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          uploadedInvoices.push(data);
          successCount++;
        }
      } catch (error) {
        errorCount++;
      }
    }

    // Refresh invoices list from server
    try {
      const invoicesRes = await api.get('/invoices');
      setInvoices(invoicesRes.data);
    } catch {
      setInvoices([...uploadedInvoices, ...invoices]);
    }
    
    setUploadDialogOpen(false);
    setSelectedClient('');
    setUploadProgress({ current: 0, total: 0 });
    
    if (successCount > 0 && errorCount === 0) {
      if (!multiMode) {
        toast.success(`${successCount} invoice${successCount > 1 ? 's' : ''} uploaded successfully`);
      }
    } else if (successCount > 0 && errorCount > 0) {
      toast.warning(`${successCount} uploaded, ${errorCount} failed`);
    } else if (errorCount > 0) {
      toast.error('Failed to upload invoices');
    }
    
    setUploading(false);
    e.target.value = '';
  };

  const handleScan = async (invoiceId) => {
    setScanning(invoiceId);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/scan`);
      setInvoices(invoices.map((inv) => (inv.id === invoiceId ? data.invoice : inv)));
      toast.success('Invoice scanned successfully');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setScanning(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;

    try {
      await api.delete(`/invoices/${id}`);
      setInvoices(invoices.filter((inv) => inv.id !== id));
      toast.success('Invoice deleted');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    return client?.name || 'Unknown';
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  const filteredInvoices = invoices.filter((invoice) => {
    const matchesSearch =
      invoice.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getClientName(invoice.client_id).toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || invoice.invoice_type === filterType;
    return matchesSearch && matchesType;
  });

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-2 mb-1">Invoices</h1>
          <p className="text-muted-foreground">Upload and manage purchase and sales invoices</p>
        </div>
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="upload-invoice-btn">
              <Upload className="w-4 h-4 mr-2" />
              Upload Invoice
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Invoice</DialogTitle>
              <DialogDescription>Select the client and invoice type, then upload the file</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Client</Label>
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger data-testid="invoice-client-select">
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Invoice Type</Label>
                <Select value={invoiceType} onValueChange={setInvoiceType}>
                  <SelectTrigger data-testid="invoice-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="purchase">Purchase Invoice</SelectItem>
                    <SelectItem value="sales">Sales Invoice</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Files</Label>
                <div className="flex items-center gap-2 mb-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="multi-invoice-toggle">
                    <input 
                      type="checkbox" 
                      checked={multiMode} 
                      onChange={(e) => setMultiMode(e.target.checked)}
                      className="rounded border-border"
                    />
                    <Files className="w-4 h-4" />
                    <span>Multi-invoice scan</span>
                  </label>
                  {multiMode && (
                    <span className="text-xs text-muted-foreground">(AI will extract all invoices from each document)</span>
                  )}
                </div>
                <div className="border-2 border-dashed border-border rounded-md p-6 text-center">
                  <input
                    type="file"
                    id="invoice-upload"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={!selectedClient || uploading}
                    multiple
                    data-testid="invoice-file-input"
                  />
                  <Button
                    variant="outline"
                    onClick={() => document.getElementById('invoice-upload').click()}
                    disabled={!selectedClient || uploading}
                    data-testid="select-invoice-file-btn"
                  >
                    {uploading 
                      ? `${multiMode ? 'Scanning' : 'Uploading'} ${uploadProgress.current}/${uploadProgress.total}...` 
                      : 'Select Files'}
                  </Button>
                  <p className="text-sm text-muted-foreground mt-2">
                    PDF, JPG, or PNG {multiMode ? '- Each file will be scanned for multiple invoices' : '- Select multiple files'}
                  </p>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search invoices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="invoice-search-input"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40" data-testid="invoice-filter-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="purchase">Purchase</SelectItem>
            <SelectItem value="sales">Sales</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Invoices Table */}
      <Card className="border border-border">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No invoices found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Client</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Net</TableHead>
                  <TableHead>VAT</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.map((invoice) => (
                  <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                    <TableCell className="font-medium">{getClientName(invoice.client_id)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {invoice.invoice_type}
                      </Badge>
                    </TableCell>
                    <TableCell>{invoice.invoice_number || '-'}</TableCell>
                    <TableCell>{invoice.supplier_name || '-'}</TableCell>
                    <TableCell>{invoice.invoice_date || '-'}</TableCell>
                    <TableCell>{invoice.net_amount ? formatCurrency(invoice.net_amount) : '-'}</TableCell>
                    <TableCell>{invoice.vat_amount ? formatCurrency(invoice.vat_amount) : '-'}</TableCell>
                    <TableCell>{formatCurrency(invoice.amount)}</TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={
                          invoice.status === 'matched'
                            ? 'status-matched'
                            : invoice.scanned
                            ? 'status-onboarded'
                            : 'status-pending'
                        }
                      >
                        {invoice.status === 'matched' ? 'Matched' : invoice.scanned ? 'Scanned' : 'Pending'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {invoice.file_path && !invoice.scanned && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleScan(invoice.id)}
                            disabled={scanning === invoice.id}
                            data-testid={`scan-invoice-${invoice.id}`}
                          >
                            {scanning === invoice.id ? (
                              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <Scan className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(invoice.id)}
                          data-testid={`delete-invoice-${invoice.id}`}
                        >
                          <Trash className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
